import { Radio } from "@headlessui/react";
import { useTranslate } from "@lobby/ocb-intl";

import { usePlayerMoneyFormatter } from "../../../../../entities";

type Commission = {
  type: "none" | "fixed" | "relative";
  value: string;
  max: string;
  min: string;
};

interface PaymentMethodCardProps {
  id: number;
  name: string;
  logoUrl: string | null;
  commission?: Commission;
  limits?: {
    min: number;
    max: number;
  };
}

export function PaymentMethodCard({
  id,
  name,
  logoUrl,
  commission,
  limits,
}: PaymentMethodCardProps) {
  return (
    <Radio as="div" value={id} className="payment-method-card">
      <div className="relative grow">
        {logoUrl ? (
          <img
            className="absolute size-full object-contain px-3.5 pt-2.5 pb-5"
            src={logoUrl}
            alt={name}
          />
        ) : null}
        {commission && <PaymentMethodCardCommission commission={commission} />}
      </div>

      {limits && <PaymentMethodCardLimits min={limits.min} max={limits.max} />}
    </Radio>
  );
}

function PaymentMethodCardCommission({ commission }: { commission: Commission }) {
  const { $t } = useTranslate();

  const formatMoney = usePlayerMoneyFormatter();

  return (
    <div
      className="absolute right-0 bottom-1 left-0 px-2.5 text-center font-medium text-10/none uppercase"
      style={{ color: "var(--text-secondary-color)" }}
    >
      {commission.type === "relative" &&
        $t({ defaultMessage: "commission: {commission}" }, { commission: `${commission.value}%` })}

      {commission.type === "fixed" &&
        $t(
          { defaultMessage: "commission: {commission}" },
          { commission: formatMoney(Number(commission.value) * 100) },
        )}
    </div>
  );
}

function PaymentMethodCardLimits({ min, max }: { min: number; max: number }) {
  const formatMoney = usePlayerMoneyFormatter();

  return (
    <div
      className="flex-center whitespace-nowrap px-2.5 py-1.5 text-center text-14/none"
      style={{ background: "var(--bg-level-0)" }}
    >
      {formatMoney(min ?? 0, { style: "decimal" })}
      {" - "}
      {formatMoney(max ?? 0)}
    </div>
  );
}
